import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  Building2,
  ChevronRight,
  CircleAlert,
  Clock3,
  CreditCard,
  Landmark,
  Info,
  LoaderCircle,
  RefreshCw,
  ReceiptText,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { formatPlainAmount, formatUsd } from "../utils/money";
import { walletApi, type BankRecord } from "../api/wallet";
import { useSession } from "../store/session";
import { useCopy } from "../i18n/copy";
import { RemoteNoticeDialog } from "../components/RemoteNoticeDialog";

function maskAccount(value: string) {
  const normalized = String(value || "").replace(/\s/g, "");
  if (normalized.includes("•")) return value;
  if (normalized.length <= 4) return normalized;
  return `•••• •••• ${normalized.slice(-4)}`;
}

function PageIntro({
  eyebrow,
  title,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  const { tr } = useCopy();
  return (
    <>
      <Link className="back-link" to="/account">
        <ArrowLeft /> {tr("Volver a mi cuenta")}
      </Link>
      <header className="page-title-row wallet-title">
        <div>
          <p className="eyebrow">{tr(eyebrow)}</p>
          <h1>{tr(title)}</h1>
        </div>
      </header>
    </>
  );
}

function Feedback({
  kind,
  children,
}: {
  kind: "success" | "error";
  children: string;
}) {
  return (
    <div className={`form-feedback ${kind}`} role="status">
      {kind === "success" ? <BadgeCheck /> : <CircleAlert />}
      <span>{children}</span>
    </div>
  );
}

export function DepositPage() {
  const { user } = useSession();
  const { tr } = useCopy();
  const location = useLocation();
  const orderType: 1 | 2 =
    new URLSearchParams(location.search).get("type") === "2" ? 2 : 1;
  const [amount, setAmount] = useState("");
  const [channelId, setChannelId] = useState<number>();
  const [reviewing, setReviewing] = useState(false);
  const [message, setMessage] = useState("");
  const [messageKind, setMessageKind] = useState<"success" | "error">(
    "success",
  );
  const channels = useQuery({
    queryKey: ["payment-channels"],
    queryFn: walletApi.paymentChannels,
  });
  const rate = useQuery({
    queryKey: ["exchange-rate"],
    queryFn: walletApi.exchangeRate,
  });
  const selected = channels.data?.find((item) => item.id === channelId);
  const numericAmount = Number(amount);
  const exchangeRate =
    Number.isFinite(rate.data) && Number(rate.data) > 0 ? Number(rate.data) : null;
  const exchangeRateLabel =
    exchangeRate === null
      ? "—"
      : tr("{{amount}} USD = {{converted}} moneda ecuatoriana", {
          amount: formatPlainAmount(1),
          converted: formatPlainAmount(exchangeRate),
        });
  const convertedAmount =
    exchangeRate !== null && Number.isFinite(numericAmount) && numericAmount > 0
      ? numericAmount * exchangeRate
      : null;
  const liveConversionLabel =
    convertedAmount === null
      ? exchangeRateLabel
      : tr("{{amount}} USD = {{converted}} moneda ecuatoriana", {
          amount: formatPlainAmount(numericAmount),
          converted: formatPlainAmount(convertedAmount),
        });
  const valid =
    Number.isFinite(numericAmount) && numericAmount > 0 && Boolean(selected);
  const payment = useMutation({
    mutationFn: () =>
      walletApi.createPayment(numericAmount, channelId!, orderType),
    onSuccess: ({ url }) => {
      setReviewing(false);
      try {
        const target = new URL(url);
        if (!["http:", "https:"].includes(target.protocol)) throw new Error();
        window.location.assign(target.toString());
      } catch {
        setMessageKind("error");
        setMessage(
          tr("La orden se creó, pero el enlace de pago recibido no es válido."),
        );
      }
    },
  });

  return (
    <main className="page wallet-page">
      <PageIntro
        eyebrow="Billetera"
        title={orderType === 2 ? "Depositar en fondos" : "Depositar fondos"}
        description="Elige un canal disponible, revisa el importe y confirma antes de salir al proveedor de pago."
      />
      <div className="wallet-layout">
        <section className="wallet-form-card">
          <div className="balance-inline">
            <span>
              <WalletCards />
              <small>{tr("Saldo disponible")}</small>
            </span>
            <strong>{formatUsd(user.balance)}</strong>
          </div>
          <label className="field-label" htmlFor="deposit-amount">
            {tr("Monto a depositar")}
          </label>
          <div className="money-input">
            <span>$</span>
            <input
              id="deposit-amount"
              inputMode="decimal"
              min="1"
              type="number"
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setReviewing(false);
                setMessage("");
              }}
              placeholder="0.00"
            />
          </div>
          <div className="quick-amounts" aria-label={tr("Montos rápidos")}>
            {[50, 100, 250, 500].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setAmount(String(value));
                  setReviewing(false);
                }}
              >
                {formatUsd(value)}
              </button>
            ))}
          </div>
          <div className="deposit-rate-card" aria-live="polite">
            <RefreshCw className={rate.isLoading ? "spin" : undefined} />
            {rate.isError ? (
              <strong className="rate-error">
                {tr("No pudimos cargar la tasa de cambio.")}
              </strong>
            ) : (
              <strong>{liveConversionLabel}</strong>
            )}
          </div>
          <fieldset className="channel-fieldset">
            <legend>{tr("Canal de pago")}</legend>
            {channels.isLoading && (
              <div className="loading-row">
                <LoaderCircle /> {tr("Consultando canales…")}
              </div>
            )}
            {channels.isError && (
              <Feedback kind="error">
                {tr("No pudimos cargar los canales de pago.")}
              </Feedback>
            )}
            {channels.data?.map((channel) => (
              <label
                className={`channel-option ${channel.id === channelId ? "selected" : ""}`}
                key={channel.id}
              >
                <input
                  type="radio"
                  name="channel"
                  checked={channel.id === channelId}
                  onChange={() => {
                    setChannelId(channel.id);
                    setReviewing(false);
                  }}
                />
                <span className="channel-icon">
                  <Landmark />
                </span>
                <span>
                  <strong>{channel.pay_name}</strong>
                </span>
                <ChevronRight />
              </label>
            ))}
          </fieldset>
          {message && <Feedback kind={messageKind}>{message}</Feedback>}
          {!reviewing ? (
            <button
              className="button primary full"
              disabled={!valid}
              onClick={() => setReviewing(true)}
            >
              {tr("Revisar depósito")} <ArrowUpRight />
            </button>
          ) : (
            <div className="confirm-panel">
              <p className="eyebrow">{tr("Confirma los datos")}</p>
              <div>
                <span>{tr("Monto")}</span>
                <strong>{formatUsd(numericAmount)}</strong>
              </div>
              <div>
                <span>{tr("Canal")}</span>
                <strong>{selected?.pay_name}</strong>
              </div>
              <div>
                <span>{tr("Cambio a moneda ecuatoriana")}</span>
                <strong>{exchangeRateLabel}</strong>
              </div>
              <div className="confirm-actions">
                <button
                  className="button secondary"
                  onClick={() => setReviewing(false)}
                >
                  {tr("Editar")}
                </button>
                <button
                  className="button primary"
                  disabled={payment.isPending}
                  onClick={() => payment.mutate()}
                >
                  {payment.isPending ? (
                    <LoaderCircle className="spin" />
                  ) : (
                    <ShieldCheck />
                  )}{" "}
                  {tr("Confirmar")}
                </button>
              </div>
              {payment.isError && (
                <Feedback kind="error">{payment.error.message}</Feedback>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export function WithdrawPage() {
  const { user } = useSession();
  const { tr } = useCopy();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<1 | 2>(1);
  const [amount, setAmount] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [success, setSuccess] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const hint = useQuery({
    queryKey: ["draw-hint"],
    queryFn: walletApi.drawHint,
  });
  const available = source === 1 ? user.balance : user.teamBalance;
  const numericAmount = Number(amount);
  const valid =
    numericAmount > 0 &&
    numericAmount <= available &&
    Boolean(hint.data?.isBindBank);
  const withdrawal = useMutation({
    mutationFn: () => walletApi.createWithdrawal(numericAmount, source),
    onSuccess: () => {
      setReviewing(false);
      setAmount("");
      queryClient.invalidateQueries({ queryKey: ["session-user"] });
      setSuccess(
        tr(
          "Solicitud enviada. Puedes seguir su estado en el historial de retiros.",
        ),
      );
    },
  });

  return (
    <main className="page wallet-page">
      <PageIntro
        eyebrow="Billetera · Retiros"
        title="Solicitar retiro"
        description="Selecciona el saldo de origen y confirma el monto que deseas recibir en tu cuenta vinculada."
      />
      <div className="wallet-page-actions">
        <Link className="button secondary" to="/wallet/withdrawals">
          <Clock3 /> {tr("Ver historial")}
        </Link>
        <button className="button secondary" onClick={() => setRulesOpen(true)}>
          <Info /> {tr("Reglas")}
        </button>
      </div>
      <div className="wallet-layout">
        <section className="wallet-form-card">
          <div className="source-tabs" role="tablist">
            <button
              className={source === 1 ? "active" : ""}
              onClick={() => {
                setSource(1);
                setReviewing(false);
              }}
            >
              {tr("Saldo personal")}
              <strong>{formatUsd(user.balance)}</strong>
            </button>
            <button
              className={source === 2 ? "active" : ""}
              onClick={() => {
                setSource(2);
                setReviewing(false);
              }}
            >
              {tr("Saldo del equipo")}
              <strong>{formatUsd(user.teamBalance)}</strong>
            </button>
          </div>
          <label className="field-label" htmlFor="withdraw-amount">
            {tr("Monto a retirar")}
          </label>
          <div className="money-input">
            <span>$</span>
            <input
              id="withdraw-amount"
              type="number"
              inputMode="decimal"
              min="1"
              max={available}
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setReviewing(false);
                setSuccess("");
              }}
              placeholder="0.00"
            />
            <button type="button" onClick={() => setAmount(String(available))}>
              {tr("Todo")}
            </button>
          </div>
          {numericAmount > available && (
            <Feedback kind="error">
              {tr("El monto supera el saldo disponible.")}
            </Feedback>
          )}
          {hint.data && !hint.data.isBindBank && (
            <div className="binding-callout">
              <CircleAlert />
              <span>
                <strong>{tr("Vincula un método de retiro")}</strong>{" "}
                {tr(
                  "Necesitas una cuenta bancaria antes de solicitar un retiro.",
                )}
              </span>
              <Link to="/wallet/payout-methods">{tr("Vincular")}</Link>
            </div>
          )}
          {success && <Feedback kind="success">{success}</Feedback>}
          {!reviewing ? (
            <button
              className="button primary full"
              disabled={!valid || hint.isLoading}
              onClick={() => setReviewing(true)}
            >
              {tr("Revisar solicitud")}
            </button>
          ) : (
            <div className="confirm-panel">
              <p className="eyebrow">{tr("Resumen del retiro")}</p>
              <div>
                <span>{tr("Origen")}</span>
                <strong>
                  {tr(source === 1 ? "Saldo personal" : "Saldo del equipo")}
                </strong>
              </div>
              <div>
                <span>{tr("Solicitud")}</span>
                <strong>{formatUsd(numericAmount)}</strong>
              </div>
              <div className="confirm-actions">
                <button
                  className="button secondary"
                  onClick={() => setReviewing(false)}
                >
                  {tr("Editar")}
                </button>
                <button
                  className="button primary"
                  disabled={withdrawal.isPending}
                  onClick={() => withdrawal.mutate()}
                >
                  {withdrawal.isPending ? (
                    <LoaderCircle className="spin" />
                  ) : (
                    <ShieldCheck />
                  )}{" "}
                  {tr("Solicitar")}
                </button>
              </div>
              {withdrawal.isError && (
                <Feedback kind="error">{withdrawal.error.message}</Feedback>
              )}
            </div>
          )}
        </section>
      </div>
      <RemoteNoticeDialog
        open={rulesOpen}
        type={10}
        onClose={() => setRulesOpen(false)}
      />
    </main>
  );
}

function withdrawalState(state: string | number) {
  if (String(state) === "1")
    return { label: "En revisión", className: "pending" };
  if (String(state) === "2") return { label: "Completado", className: "done" };
  return { label: "Rechazado", className: "rejected" };
}

const fundTypeNames: Record<string, string> = {
  "1": "Depósito",
  "2": "Retiro",
  "3": "Ajuste de puntos",
  "4": "Ajuste de saldo",
  "5": "Compra de equipo de minería",
  "6": "Ingreso de minería",
  "7": "Beneficio de equipo",
  "8": "Aporte a fondos",
  "9": "Ingreso de fondos",
  "10": "Bono de primer depósito",
  "11": "Canje de CDK",
  "12": "Canje de puntos a saldo",
  "13": "Canje de saldo a puntos",
  "14": "Puntos usados en sorteo",
  "15": "Puntos ganados en sorteo",
  "16": "Premio de sorteo",
  "17": "Bono de registro",
  "18": "Experiencia obtenida",
  "19": "Puntos por ingreso",
  "20": "Puntos por depósito",
  "21": "Beneficio de invitación",
  "22": "Venta de equipo",
  "23": "Ingreso de fondos",
  "24": "Puntos por compra del equipo",
  "25": "Puntos por compra",
  "26": "Ingreso de potencia de cómputo",
  "27": "Transferencia de ingreso del equipo",
};

export function TransactionsPage() {
  const location = useLocation();
  const { tr } = useCopy();
  const [tab, setTab] = useState<"funds" | "withdrawals">(
    new URLSearchParams(location.search).get("tab") === "withdrawals"
      ? "withdrawals"
      : "funds",
  );
  const fundPageSize = 10;
  const funds = useInfiniteQuery({
    queryKey: ["fund-records"],
    queryFn: ({ pageParam }) => walletApi.fundRecords(pageParam, fundPageSize),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === fundPageSize ? pages.length + 1 : undefined,
  });
  const withdrawals = useQuery({
    queryKey: ["withdrawal-records"],
    queryFn: () => walletApi.withdrawalRecords(),
  });
  const loading = tab === "funds" ? funds.isLoading : withdrawals.isLoading;
  const error = tab === "funds" ? funds.isError : withdrawals.isError;
  const fundRecords = funds.data?.pages.flat() || [];
  return (
    <main className="page wallet-page">
      <PageIntro
        eyebrow="Actividad"
        title="Movimientos"
        description="Consulta depósitos, ingresos, compras y solicitudes de retiro desde un solo lugar."
      />
      <div className="record-card">
        <div className="segmented record-tabs">
          <button
            className={tab === "funds" ? "active" : ""}
            onClick={() => setTab("funds")}
          >
            <ReceiptText /> {tr("Movimientos")}
          </button>
          <button
            className={tab === "withdrawals" ? "active" : ""}
            onClick={() => setTab("withdrawals")}
          >
            <ArrowUpRight /> {tr("Retiros")}
          </button>
        </div>
        {loading && (
          <div className="loading-row">
            <LoaderCircle /> {tr("Cargando historial…")}
          </div>
        )}
        {error && (
          <Feedback kind="error">
            {tr("No pudimos cargar el historial.")}
          </Feedback>
        )}
        <div className={tab === "funds" ? "balance-ledger" : "record-list"}>
          {tab === "funds"
            ? fundRecords.map((item, index) => {
                const delta =
                  Number(item.after_amount) - Number(item.before_amount);
                const signedAmount = `${delta >= 0 ? "+" : "−"}${formatUsd(Math.abs(delta))}`;
                return (
                  <article
                    className="balance-ledger-item"
                    key={`${item.create_time}-${index}`}
                  >
                    <span
                      className={`ledger-node ${delta >= 0 ? "credit" : "debit"}`}
                    >
                      <ReceiptText />
                    </span>
                    <div className="ledger-content">
                      <div className="ledger-head">
                        <div>
                          <strong>
                            {tr(
                              fundTypeNames[String(item.type)] || "Movimiento",
                            )}{" "}
                            {fundTypeNames[String(item.type)] ? "" : item.type}
                          </strong>
                          <small>{item.create_time}</small>
                        </div>
                        <strong
                          className={`ledger-delta ${delta >= 0 ? "positive" : "negative"}`}
                        >
                          {signedAmount}
                        </strong>
                      </div>
                      <div className="ledger-flow">
                        <span>
                          <small>{tr("Saldo anterior")}</small>
                          <strong>
                            {formatUsd(Number(item.before_amount))}
                          </strong>
                        </span>
                        <ChevronRight />
                        <span>
                          <small>{tr("Saldo posterior")}</small>
                          <strong>
                            {formatUsd(Number(item.after_amount))}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })
            : withdrawals.data?.map((item, index) => {
                const state = withdrawalState(item.state);
                return (
                  <article
                    className="record-row"
                    key={`${item.create_time_format}-${index}`}
                  >
                    <span className="record-icon debit">
                      <ArrowUpRight />
                    </span>
                    <div>
                      <strong>{tr("Retiro")}</strong>
                      <small>{item.create_time_format}</small>
                    </div>
                    <span className="record-amount">
                      <strong>
                        {formatUsd(Number(item.real_amount || item.amount))}
                      </strong>
                      <small className={`state-pill ${state.className}`}>
                        {tr(state.label)}
                      </small>
                    </span>
                  </article>
                );
              })}
        </div>
        {!loading &&
          (tab === "funds"
            ? !fundRecords.length
            : !withdrawals.data?.length) && (
            <div className="empty-state">
              <ReceiptText />
              <h2>{tr("Aún no hay movimientos")}</h2>
            </div>
          )}
        {tab === "funds" && funds.hasNextPage && (
          <button
            className="button secondary full ledger-more"
            disabled={funds.isFetchingNextPage}
            onClick={() => funds.fetchNextPage()}
          >
            {funds.isFetchingNextPage ? (
              <LoaderCircle className="spin" />
            ) : (
              <RefreshCw />
            )}
            {tr("Cargar más")}
          </button>
        )}
      </div>
    </main>
  );
}

export function WithdrawalHistoryPage() {
  const { tr } = useCopy();
  const pageSize = 10;
  const summary = useQuery({
    queryKey: ["withdrawal-summary"],
    queryFn: walletApi.withdrawalSummary,
  });
  const recordsQuery = useInfiniteQuery({
    queryKey: ["withdrawal-history"],
    queryFn: ({ pageParam }) =>
      walletApi.withdrawalRecords(pageParam, pageSize),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === pageSize ? pages.length + 1 : undefined,
  });
  const records = recordsQuery.data?.pages.flat() || [];

  return (
    <main className="page wallet-page withdrawal-history-page">
      <Link className="back-link" to="/wallet/withdraw">
        <ArrowLeft /> {tr("Volver a retirar")}
      </Link>
      <header className="page-title-row wallet-title">
        <div>
          <p className="eyebrow">{tr("Billetera")}</p>
          <h1>{tr("Historial de retiros")}</h1>
        </div>
        <span className="withdrawal-title-icon">
          <Clock3 />
        </span>
      </header>

      {summary.isLoading ? (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando…")}
        </div>
      ) : summary.isError ? (
        <Feedback kind="error">{tr("No pudimos cargar el resumen.")}</Feedback>
      ) : (
        <section className="withdrawal-summary">
          <div className="withdrawal-balance">
            <span>
              <WalletCards />
            </span>
            <div>
              <small>{tr("Saldo disponible")}</small>
              <strong>{formatUsd(Number(summary.data?.balance || 0))}</strong>
            </div>
          </div>
          <div className="withdrawal-stat pending">
            <small>{tr("En revisión")}</small>
            <strong>
              {formatUsd(Number(summary.data?.reviewBalance || 0))}
            </strong>
          </div>
          <div className="withdrawal-stat done">
            <small>{tr("Total recibido")}</small>
            <strong>{formatUsd(Number(summary.data?.drawBalance || 0))}</strong>
          </div>
        </section>
      )}

      <section className="withdrawal-record-card">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{tr("Actividad")}</p>
            <h2>{tr("Solicitudes")}</h2>
          </div>
        </div>
        {recordsQuery.isLoading && (
          <div className="loading-row">
            <LoaderCircle className="spin" /> {tr("Cargando historial…")}
          </div>
        )}
        {recordsQuery.isError && (
          <Feedback kind="error">
            {tr("No pudimos cargar el historial.")}
          </Feedback>
        )}
        {!recordsQuery.isLoading &&
          !recordsQuery.isError &&
          records.length === 0 && (
            <div className="empty-state compact-empty">
              <ReceiptText />
              <h2>{tr("Aún no hay retiros")}</h2>
            </div>
          )}
        {records.length > 0 && (
          <div className="withdrawal-record-list">
            {records.map((item, index) => {
              const state = withdrawalState(item.state);
              return (
                <article key={`${item.create_time_format}-${index}`}>
                  <span className={`withdrawal-record-mark ${state.className}`}>
                    <ArrowUpRight />
                  </span>
                  <div>
                    <strong>{formatUsd(Number(item.amount))}</strong>
                    <small>{item.create_time_format}</small>
                  </div>
                  <div className="withdrawal-record-result">
                    <span className={`state-pill ${state.className}`}>
                      {tr(state.label)}
                    </span>
                    <small>
                      {tr("Recibido")}{" "}
                      {formatUsd(Number(item.real_amount || 0))}
                    </small>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      {recordsQuery.hasNextPage && (
        <button
          className="button secondary full withdrawal-more"
          disabled={recordsQuery.isFetchingNextPage}
          onClick={() => recordsQuery.fetchNextPage()}
        >
          {recordsQuery.isFetchingNextPage ? (
            <LoaderCircle className="spin" />
          ) : (
            <RefreshCw />
          )}
          {tr("Cargar más")}
        </button>
      )}
    </main>
  );
}

export function PayoutMethodsPage() {
  const queryClient = useQueryClient();
  const { tr } = useCopy();
  const bankQuery = useQuery({
    queryKey: ["user-bank"],
    queryFn: walletApi.userBank,
  });
  const optionsQuery = useQuery({
    queryKey: ["bank-options"],
    queryFn: walletApi.bankOptions,
  });
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState("");
  const [form, setForm] = useState<BankRecord>({
    firstName: "",
    phone: "",
    bankName: "",
    bankCode: "",
    bankCard: "",
    ext: "",
    bankId: "",
  });
  useEffect(() => {
    if (bankQuery.data?.bank) setForm(bankQuery.data.bank);
  }, [bankQuery.data]);
  const selectedName = useMemo(
    () =>
      optionsQuery.data?.find((item) => item.code === form.bankCode)?.name ||
      form.bankName,
    [optionsQuery.data, form.bankCode, form.bankName],
  );
  const save = useMutation({
    mutationFn: () => walletApi.saveBank({ ...form, bankName: selectedName }),
    onSuccess: () => {
      setEditing(false);
      setSaved(tr("Método de retiro guardado correctamente."));
      queryClient.invalidateQueries({ queryKey: ["user-bank"] });
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  const bank = bankQuery.data?.bank;
  return (
    <main className="page wallet-page">
      <PageIntro
        eyebrow="Seguridad financiera"
        title="Método de retiro"
        description="Administra la cuenta bancaria donde recibirás tus retiros."
      />
      <div className="wallet-layout">
        <section className="wallet-form-card">
          {bankQuery.isLoading ? (
            <div className="loading-row">
              <LoaderCircle /> {tr("Consultando cuenta…")}
            </div>
          ) : bank && !editing ? (
            <>
              <div className="bank-card-visual">
                <span>
                  <Building2 />
                </span>
                <p>GOP ALA · {tr("CUENTA VINCULADA")}</p>
                <strong>{maskAccount(bank.bankCard)}</strong>
                <div>
                  <span>{bank.firstName}</span>
                  <span>{bank.bankName}</span>
                </div>
              </div>
              <div className="verified-line">
                <BadgeCheck />
                <span>
                  <strong>{tr("Cuenta registrada")}</strong>
                </span>
              </div>
              {saved && <Feedback kind="success">{saved}</Feedback>}
              <button
                className="button secondary full"
                disabled={bankQuery.data?.isAllowEdit === false}
                onClick={() => setEditing(true)}
              >
                {tr("Editar información")}
              </button>
              {bankQuery.data?.isAllowEdit === false && (
                <p className="fine-print">
                  {tr(
                    "La edición está bloqueada por la configuración de seguridad.",
                  )}
                </p>
              )}
            </>
          ) : (
            <form className="bank-form" onSubmit={submit}>
              <label className="field-label">
                {tr("Nombre del titular")}
                <input
                  required
                  value={form.firstName}
                  onChange={(e) =>
                    setForm({ ...form, firstName: e.target.value })
                  }
                />
              </label>
              <label className="field-label">
                {tr("Teléfono")}
                <input
                  required
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </label>
              <label className="field-label">
                {tr("Institución financiera")}
                <select
                  required
                  value={form.bankCode}
                  onChange={(e) => {
                    const option = optionsQuery.data?.find(
                      (item) => item.code === e.target.value,
                    );
                    setForm({
                      ...form,
                      bankCode: e.target.value,
                      bankName: option?.name || "",
                    });
                  }}
                >
                  <option value="">{tr("Selecciona un banco")}</option>
                  {optionsQuery.data?.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-label">
                {tr("Número de cuenta")}
                <input
                  required
                  inputMode="numeric"
                  value={form.bankCard}
                  onChange={(e) =>
                    setForm({ ...form, bankCard: e.target.value })
                  }
                />
              </label>
              <label className="field-label">
                {tr("Tipo o detalle de cuenta")} <small>{tr("Opcional")}</small>
                <input
                  value={form.ext || ""}
                  onChange={(e) => setForm({ ...form, ext: e.target.value })}
                  placeholder={tr("Ej. Cuenta de ahorros")}
                />
              </label>
              {save.isError && (
                <Feedback kind="error">{save.error.message}</Feedback>
              )}
              <div className="confirm-actions">
                {bank && (
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => {
                      setEditing(false);
                      setForm(bank);
                    }}
                  >
                    {tr("Cancelar")}
                  </button>
                )}
                <button className="button primary" disabled={save.isPending}>
                  {save.isPending ? (
                    <LoaderCircle className="spin" />
                  ) : (
                    <ShieldCheck />
                  )}{" "}
                  {tr("Guardar")}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}
